import { conditionFields, conditionOps, policyRuleDecisions } from "@veritrellis/shared";
import type { ActionPolicy } from "@veritrellis/shared";
import {
  defaultBuilderState,
  type BuilderCondition,
  type BuilderRule,
  type BuilderState,
  type ConditionField,
  type ConditionOp,
  type PolicyRuleDecision
} from "./builderTypes.js";

export type ParseResult = {
  state: BuilderState;
  /** false when the policy contains constructs the builder cannot represent */
  compatible: boolean;
};

const isValidField = (v: unknown): v is ConditionField =>
  typeof v === "string" && (conditionFields as readonly string[]).includes(v);

const isValidOp = (v: unknown): v is ConditionOp =>
  typeof v === "string" && (conditionOps as readonly string[]).includes(v);

const isValidDecision = (v: unknown): v is PolicyRuleDecision =>
  typeof v === "string" && (policyRuleDecisions as readonly string[]).includes(v);

const isConditionValue = (v: unknown): v is string | number | string[] => {
  if (typeof v === "string" || typeof v === "number") {
    return true;
  }
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) {
    return true;
  }
  return false;
};

/**
 * Parses a stored ActionPolicy back into BuilderState.
 * Returns compatible=false if any rule contains constructs
 * the Phase 1 builder cannot express.
 */
export const parseToBuilder = (policy: ActionPolicy): ParseResult => {
  const state: BuilderState = defaultBuilderState();
  state.mode = policy.mode;
  state.autoAllowLowRisk = policy.auto_allow_low_risk ?? false;
  state.defaultDecision = policy.default_decision ?? "allow";

  if (policy.mode !== "conditional" || policy.rules == null) {
    return { state, compatible: true };
  }

  let compatible = true;

  const rules: BuilderRule[] = policy.rules.map((rule) => {
    const conditions: BuilderCondition[] = [];

    for (const cond of rule.when) {
      if (!isValidField(cond.field) || !isValidOp(cond.op) || !isConditionValue(cond.value)) {
        compatible = false;
        continue;
      }
      conditions.push({
        id: crypto.randomUUID(),
        field: cond.field,
        op: cond.op,
        value: cond.value
      });
    }

    if (!isValidDecision(rule.then)) {
      compatible = false;
    }

    return {
      id: crypto.randomUUID(),
      conditions,
      decision: isValidDecision(rule.then) ? rule.then : "deny",
      modifications: rule.modifications,
      approverGroupId: rule.approver_group_id ?? null,
      permitTtlSeconds: rule.permit_ttl_seconds ?? null
    };
  });

  state.rules = rules;
  return { state, compatible };
};

/**
 * Safely parses a raw JSON string into a ParseResult.
 * Returns null if the JSON itself is invalid.
 */
export const parseJsonToBuilder = (raw: string): ParseResult | null => {
  try {
    const parsed = JSON.parse(raw) as ActionPolicy;
    return parseToBuilder(parsed);
  } catch {
    return null;
  }
};
